//@flow
import * as React from 'react';
import EntityForm from './EntityForm';
import EntityAdapter from './EntityAdapter';

import { connect } from 'react-redux';
import {
    clientSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { withRouter } from 'common/components/core/router';
import {
    getEntitySettings,
    getTaskSettings
} from 'common/redux/selectors/clientSelector';
import { getPlatformSettings } from 'common/modules/platform-settings';
import PromiseUtils, { PromiseStatus } from 'common/utils/PromiseUtils';
import { translate } from 'react-i18next';
import { ValidationStatusEnum } from 'common/entity/generic/CodeInputWithId';
import DataUtils from 'common/utils/DataUtils';
import Enum from 'common/enums/Enum';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import ImmutableModel from 'common/models/flow/ImmutableModel';
import { LocusApiModel, ApiError } from 'common/modules/api';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { LocaleSettings } from 'common/models/internal/LocaleSettings';
import type { TaskSettings } from 'common/models/internal/Client';
import type { TFunction } from 'react-i18next';
import isFunction from 'lodash/isFunction';
export type Field = {
    accessor: string,
    label?: string,
    helpText?: string,
    placeholder?: string,
    disabledOnEdit?: boolean,
    required?: boolean,
    halfWidth?: boolean,
    type?: FieldTypeEnum,
    component?: React.ComponentType<any>,
    onChangeHandler?: Function,
    extraProps?: Object => Object,
    options?: Array<*> | (Object => Object)
};

type MasterSingleEditFactory = {
    FIELDS: Array<Field>,
    MasterEntity: any,
    extraApiParams: Object,
    INITIAL_FETCH_FIELDS?: Array<string>,
    options: {
        ENTITY_NAME_ID: string,
        ENTITY_SETTINGS_NAME: string,
        PARAM_ID: string,
        preferPutOverPatch?: boolean
    },
    mapEntityStateToProps: Object => Object,
    EntityFetchAPI: LocusApiModel,
    MasterActions: any,
    processFetchedEntity?: Object => Object
};

type Props = {
    clientId: string,
    entityId: string,
    hiddenModelProperties: Array<string>,
    isEditing: boolean,
    addEntity: Function,
    updateEntity: Function,
    onDismiss: Function,
    entitySettings: EntitySettings,
    localeSettings: LocaleSettings,
    taskSettings: TaskSettings,
    specificProps: Object,
    validateId: Function,
    isFieldHidden: () => boolean,
    processEntity: ImmutableModel => ImmutableModel,
    t: TFunction
};

type State = {
    allowSave: boolean,
    isRequiredDataFetched: boolean,
    dataFetchError: ?string,
    fields: Array<Field>,
    isSaving: boolean,
    defaultEntity: any
};

const EntityStatusEnum = new Enum({
    Inactive: 'INACTIVE',
    Active: 'ACTIVE'
});

const entityMasterSingleEditFactory = ({
    MasterEntity,
    MasterActions,
    EntityFetchAPI,
    extraApiParams,
    FIELDS,
    INITIAL_FETCH_FIELDS,
    options,
    mapEntityStateToProps,
    processFetchedEntity
}: MasterSingleEditFactory) => {
    const {
        ENTITY_NAME_ID,
        ENTITY_SETTINGS_NAME,
        PARAM_ID,
        preferPutOverPatch
    } = options;

    class SingleMasterEntityEditContainer extends React.PureComponent<
        Props,
        State
    > {
        constructor(props) {
            super(props);
            this.FILTERED_FIELDS = EntityAdapter.filterOutHiddenFields(
                FIELDS,
                props
            );
            this.fieldsOrder = this.FILTERED_FIELDS.map(
                field => field.accessor
            );
            this.ENTITY_FIELDS = EntityAdapter.getFieldsWithComponent({
                fields: this.FILTERED_FIELDS
            });

            this.state = {
                allowSave: !DataUtils.isEmpty(props.entityId) ? true : false,
                isRequiredDataFetched: false,
                dataFetchError: null,
                fields: this.ENTITY_FIELDS,
                isSaving: false,
                defaultEntity: this.initializeDefaultEntity(props)
            };
        }

        componentDidMount() {
            this.fetchResources();
        }

        componentDidUpdate(prevProps) {
            if (
                this.props.clientId !== prevProps.clientId ||
                this.props.entityId !== prevProps.entityId
            ) {
                this.fetchResources();
            }

            /**
             * FIX: ES-6701
             * allowSave incase user navigates from Add Entity flow, to Edit flow
             */
            if (this.props.entityId && !prevProps.entityId) {
                this.setState({
                    allowSave: true
                });
            }
            /**
             * FIX
             * reset defaultEntity
             */
            if (!this.props.entityId && prevProps.entityId) {
                this.setState({
                    defaultEntity: this.initializeDefaultEntity(this.props)
                });
            }
        }

        initializeDefaultEntity = props => {
            return MasterEntity.defaultInit({
                props: this.getMapStateProps()
            })
                .setClientId(props.clientId)
                .setHiddenModelProperties(props.hiddenModelProperties);
        };

        fetchEntity = () => {
            const { clientId, entityId, hiddenModelProperties } = this.props;
            if (!entityId) {
                throw new Error(`${PARAM_ID} expected`);
            }

            let params = { clientId };
            params[PARAM_ID] = entityId;

            if (extraApiParams) {
                params = { ...params, ...extraApiParams };
            }

            return EntityFetchAPI.fetch(params).then(entity => {
                const { defaultEntity } = this.state;
                let updatedEntity = MasterEntity.fromJS(
                    entity
                ).setHiddenModelProperties(hiddenModelProperties);

                updatedEntity = defaultEntity.mergeDeep(updatedEntity);

                if (isFunction(updatedEntity.setValidationBasedOnEntityData)) {
                    updatedEntity = updatedEntity.setValidationBasedOnEntityData();
                }
                return processFetchedEntity && isFunction(processFetchedEntity)
                    ? processFetchedEntity({
                          entity: updatedEntity,
                          props: this.props
                      })
                    : updatedEntity;
            });
        };

        fetchResources = async () => {
            const { defaultEntity } = this.state;
            const { entityId, t } = this.props;

            this.setState({
                isRequiredDataFetched: false,
                dataFetchError: null
            });

            try {
                if (entityId) {
                    const entity = await this.fetchEntity();
                    const fetchedFields = await this.fetchInitialFields({
                        entity
                    });

                    this.setState({
                        isRequiredDataFetched: true,
                        defaultEntity: entity,
                        fields: { ...this.state.fields, ...fetchedFields }
                    });
                    return;
                }
                const fetchedFields = await this.fetchInitialFields({
                    entity: defaultEntity
                });

                this.setState({
                    isRequiredDataFetched: true,
                    fields: { ...this.state.fields, ...fetchedFields }
                });
            } catch (error) {
                if (error instanceof ApiError) {
                    this.setState({ dataFetchError: error.message });
                    return;
                }
                this.setState({ dataFetchError: t('genericDataFetchError') });
            }
        };

        fetchInitialFields = ({ entity }) => {
            const { fields } = this.state;

            const promisesToProcess = [];
            for (const key in INITIAL_FETCH_FIELDS) {
                const accessor = INITIAL_FETCH_FIELDS[key];
                const field = fields[accessor];
                if (field) {
                    promisesToProcess[accessor] = field.fetchInfo.fetcher(
                        field.fetchInfo.params({
                            field,
                            entity,
                            props: this.getMapStateProps()
                        })
                    );
                }
            }

            return PromiseUtils.processAll(promisesToProcess).then(results => {
                const { fields } = this.state;

                const updatedFields = {};
                results.forEach(result => {
                    const accessor = result.id;
                    if (result.status !== PromiseStatus.RESOLVED) {
                        throw result.error;
                    }
                    const field = { ...fields[accessor] };
                    field.fetchInfo.response = result.response;
                    updatedFields[accessor] = field;
                });
                return updatedFields;
            });
        };

        clearDataFetchError = () => {
            // Clear message after 5 sec
            setTimeout(() => {
                this.setState({
                    dataFetchError: null
                });
            }, 5000);
        };

        handleSubmit = ({ updatedEntity }) => {
            const { defaultEntity } = this.state;
            const {
                entityId,
                addEntity,
                updateEntity,
                clientId,
                onDismiss
            } = this.props;

            let savingPromise = null;

            const params = {
                clientId
            };

            params[PARAM_ID] = updatedEntity.id;

            this.setState({ isSaving: true });

            if (entityId && !preferPutOverPatch) {
                // If defaultEntity is present, then its an edit

                /**
                 * Case if we are updating a disabled entity
                 * (User can enter a code of disabled entity during entity creation)
                 * Assuming every master entity would have status field for enable/disable
                 */
                let entity = updatedEntity;
                if (entity.status === EntityStatusEnum.Inactive) {
                    entity = entity.setStatus(EntityStatusEnum.Active);
                }
                savingPromise = updateEntity(
                    params,
                    defaultEntity.defaultSanitize(
                        defaultEntity,
                        this.getMapStateProps()
                    ),
                    entity,
                    { dontInjectError: true }
                );
            } else if (entityId && preferPutOverPatch) {
                //If entity doesnt support patch
                savingPromise = addEntity(
                    { overwrite: true, ...params },
                    updatedEntity
                );
            } else {
                savingPromise = addEntity(params, updatedEntity);
            }
            savingPromise
                .then(() => {
                    onDismiss(true);
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        this.setState(
                            {
                                isSaving: false,
                                dataFetchError: error.message
                            },
                            this.clearDataFetchError
                        );
                        return;
                    } else {
                        throw error;
                    }
                });
        };

        getMapStateProps = () => {
            const {
                clientId,
                entityId,
                entitySettings,
                taskSettings,
                localeSettings,
                specificProps,
                validateId,
                isFieldHidden
            } = this.props;
            let props = {
                clientId,
                entityId,
                entitySettings,
                localeSettings,
                taskSettings,
                isFieldHidden,
                validateDuplicateId: validateId,
                onValidateDuplicateIdChange: this.onValidateDuplicateIdChange
            };
            if (specificProps) {
                props = { ...props, ...specificProps };
            }

            return props;
        };

        onSubmit = ({ updatedEntity }) => {
            const entity = this.processEntity(updatedEntity);
            this.handleSubmit({ updatedEntity: entity });
        };

        processEntity = updatedEntity => {
            const { defaultEntity } = this.state;
            const sanitizedEntity = updatedEntity.defaultSanitize
                ? updatedEntity.defaultSanitize(
                      defaultEntity,
                      this.getMapStateProps()
                  )
                : updatedEntity;

            // hook to do any processing to entity before add/patch
            const { processEntity } = this.props;
            return (
                (processEntity && processEntity(sanitizedEntity)) ||
                sanitizedEntity
            );
        };

        onValidateDuplicateIdChange = idStatus => {
            if (idStatus === ValidationStatusEnum.NewId) {
                this.setState({ allowSave: true });
            } else {
                this.setState({ allowSave: false });
            }
        };

        render() {
            const {
                onDismiss,
                entitySettings,
                isEditing,
                t,
                testId
            } = this.props;
            const {
                isRequiredDataFetched,
                fields,
                defaultEntity,
                dataFetchError,
                isSaving,
                allowSave
            } = this.state;
            return (
                <EntityForm
                    testId={testId}
                    shouldAllowSave={allowSave}
                    name={ENTITY_NAME_ID}
                    t={t}
                    entity={defaultEntity}
                    order={this.fieldsOrder}
                    isEditing={isEditing}
                    fields={fields}
                    onSubmit={this.onSubmit}
                    isLoading={!isRequiredDataFetched}
                    isSaving={isSaving}
                    onDismiss={onDismiss}
                    getMappedProps={this.getMapStateProps}
                    errorMessage={dataFetchError}
                    fetchResources={this.fetchResources}
                    entitySettings={entitySettings}
                    settingsName={ENTITY_SETTINGS_NAME}
                />
            );
        }
    }

    const mapStateToProps = (state, props) => {
        const clientId = clientSelector(state, props);
        const entityId = editEntityIdSelector(state, props);
        const entitySettings = getEntitySettings(state, props);
        const taskSettings = getTaskSettings(state, props);
        const { localeSettings } = getPlatformSettings();

        let hiddenModelProperties = [];
        if (entitySettings) {
            hiddenModelProperties = entitySettings.getHiddenModelProperties(
                ENTITY_SETTINGS_NAME
            );
        }
        const entityProps = {
            clientId,
            entityId,
            isEditing: Boolean(entityId),
            entitySettings,
            localeSettings,
            taskSettings,
            hiddenModelProperties
        };

        if (mapEntityStateToProps) {
            entityProps.specificProps = mapEntityStateToProps({
                state,
                props,
                baseProps: entityProps
            });
        }
        return entityProps;
    };

    const actions = {
        addEntity: MasterActions.addEntity,
        updateEntity: MasterActions.patchEntity,
        validateId: MasterActions.validateEntityIds
    };

    return withRouter(
        connect(
            mapStateToProps,
            actions
        )(translate('entityManagement')(SingleMasterEntityEditContainer))
    );
};

export { entityMasterSingleEditFactory };
